import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import {
  useToast,
  Flex,
  Text,
  Button,
  CloseButton,
  useColorMode,
} from "@chakra-ui/react";
import { UnsupportedChainIdError, useWeb3React } from "@web3-react/core";
import { Web3Provider } from "@ethersproject/providers";
import { useIdle } from "react-use";
import _isEmpty from "lodash/isEmpty";
import _keys from "lodash/keys";
import _values from "lodash/values";
import { Userpilot } from "userpilot";

// Components
import Loader from "components/Loader";
import WalletConnectModal from "components/WalletConnectModal";
import AccountModal from "components/AccountModal";
import TransactionModal from "components/TransactionModal";
import AuditInfoModal from "components/AuditInfoModal";
import AllTransaction from "components/AllTransactions";

// Hooks
import useEagerConnect from "hooks/useEagerConnect";
import useInactiveListener from "hooks/useInactiveListener";
import useLocalStorage from "hooks/useLocalStorage";
import useInterval from "hooks/useInterval";
import useLedgerApp from "hooks/useLedgerApp";

// Constants
import {
  getWalletOptions,
  walletIconMapping,
  walletLabelMapping,
} from "constants/walletMenuOptions";
import { APPROVAL_AMOUNT } from "constants/contracts";
import { TXN_STATUSES, TXN_TYPES, TXN_STATUS } from "constants/transactions";
import {
  CLICK_STAKE_ADD_TOKEN,
  CLICK_CLAIM_BTN,
  CLOSE_WALLET_MODAL,
  COPY_ACCOUNT_ADDRESS,
  HANDLE_ACTIVATE,
  HANDLE_APPROVE,
  HANDLE_DISCONNECT,
  HANDLE_SWITCH_NETWORK,
  OPEN_ACCOUNT_MODAL,
  OPEN_AUDIT_MODAL,
  OPEN_WALLET_MODAL,
  SUBMIT_STAKE,
  VIEW_ADDRESS_ON_SCANNER,
  VIEW_TXN_ON_SCANNER,
  PURCHASE_EVENT,
  TRANSACTION_FAILED,
  TRANSACTION_SUCCESS,
  ACTIVATE_SUCCESS,
  ACTIVATE_FAILED,
  CLEAR_PAST_TRANSACTIONS,
  STAKE_SUCCESS,
} from "constants/analytics";

// Config
import { currentNetworkInfo } from "config/network";

// Utils
import { getErrorMessage } from "utils/errorMessage";
import { copyToClipboard } from "utils/common";
import { sentryErrorLog } from "utils/sentryLog";
import { trackEvent } from "utils/firebase";
import { getConnector, isZerion } from "utils/connectors";

// Services
import {
  getAppData,
  getTransactionConfirmation,
  getWalletData,
  switchToMainnet,
} from "services/common";
import { addApproval } from "services/matic";
import {
  addApprovalMaticX,
  addXTokenToWallet,
  claimWithdrawal,
  requestWithdraw,
  submitTransaction,
} from "services/maticx";

// Types
import { Withdrawal } from "types/common";

// Types
import { NetworkConfigType } from "config/network/types";
import {
  AnalyticsCallOptions,
  EventNameString,
  EventParams,
} from "firebase/analytics";
import { BigNumber } from "ethers";

type AppData = {
  tvl: number;
  conversionRate: number;
  conversionFromRate: number;
  currentEpoch?: number;
  crossChainTVL: string;
  instantPool: number;
  instantPoolMatic: number;
  stakingLimitDetails?: {
    totalLimit: number;
    exhaustedLimit: number;
  };
};

type WalletData = {
  tokenBalance: string;
  xTokenBalance: string;
  maxAllowance?: string;
  maticXAllowance?: string;
  withdrawals?: Withdrawal[];
};

type TxnData = {
  hash: string;
  status: TXN_STATUS;
  time: Date;
  amount: string | null;
  token: string;
  type: string;
};

type TxnDataMap = {
  [key: string]: TxnData;
};

const emptyFn = () => {
  undefined;
};

/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
const initialData = {
  selectedChain: "",
  toggleChain: emptyFn,
  config: {} as NetworkConfigType,
  isPolygonChainActive: true,
  walletInfo: {
    tokenBalance: 0,
    tokenSymbol: "",
    xTokenBalance: 0,
    xTokenSymbol: "",
    blockchain: "",
    icon: "",
    xtokenIcon: "",
    address: "",
  },
  providerInfo: {
    name: "",
    icon: "",
  },
  handleActivate: (k: string, cb: any) => {},
  handleDisconnect: emptyFn,
  handleAddressCopy: emptyFn,
  handleViewOnEtherScan: emptyFn,
  handleApprove: emptyFn,
  handleStake: (amt: string) => {},
  handleUnstake: (amt: string) => {},
  handleClaim: (id: number, amt: number) => {},
  addTokenToUserWallet: emptyFn,
  openWalletModal: (loc: string) => {},
  openAccountModal: emptyFn,
  openAuditModal: emptyFn,
  closeModal: emptyFn,
  loading: false,
  balLoading: false,
  tvl: 0,
  conversionRate: 1,
  conversionFromRate: 1,
  maxAllowance: "",
  maticXAllowance: "",
  withdrawals: [{} as Withdrawal],
  crossChainTVL: "",
  instantPool: 0,
  instantPoolMatic: 0,
  isTxnProcessing: false,
  isTxnModalOpen: false,
  openTxnHistoryModal: emptyFn,
  stakingLimitDetails: {
    totalLimit: 0,
    exhaustedLimit: 0,
  },
  isValidChainSelected: true,
  switchToPolygon: emptyFn,
  connectorName: "",
  isLedgerApp: false,
  track: (
    eventName: EventNameString | string,
    eventParams: EventParams = {},
    options?: AnalyticsCallOptions
  ) => {},
};

const providerInitialData = {
  modalType: "",
  appData: {} as AppData,
  walletData: {} as WalletData,
  appLoading: true,
  balLoading: true,
  txnLoader: false,
  isTxnBatchOpen: false,
  activatingConnector: undefined as any,
};

type PersistantDataType = {
  connectorPref: string;
  actionPref: string;
  promptError: string;
  txnBatch: TxnDataMap;
  latestTxnHash: string;
};

const initialPersistantData = {
  connectorPref: "",
  actionPref: "CONNECT",
  promptError: "",
  txnBatch: {} as TxnDataMap,
  latestTxnHash: "",
};

export const StakeContext = createContext(initialData);

const STADER_APPLICATION_CONTEXT = "stader-app-pers-data-store";

const REFRESH_INTERVAL = 10000;
const REFRESH_INTERVAL_TXN = 5000;

const INJECTED_WALLETS = [
  "injected",
  "okex",
  "coin98",
  "safepal",
  "mathwallet",
  "trustwallet",
  "bitkeep",
  "phantom",
  "exodus",
];

export const StakeProvider = ({
  chainDetails,
  children,
}: {
  chainDetails: {
    selectedChain: string;
    toggleChain: () => void;
    config: NetworkConfigType;
    isPolygonChainActive: boolean;
    setSelectedChain: (ch: string | number) => void;
  };
  children: ReactNode;
}) => {
  const {
    selectedChain,
    toggleChain,
    config,
    isPolygonChainActive,
    setSelectedChain,
  } = chainDetails;
  const { active, connector, library, account, chainId, activate, deactivate } =
    useWeb3React<Web3Provider>();

  const { colorMode } = useColorMode();

  const isIdle = useIdle(150e3);

  const [providerData, setProviderData] = useState(providerInitialData);

  const [persistantData, setPersistantData] = useLocalStorage(
    STADER_APPLICATION_CONTEXT,
    initialPersistantData as PersistantDataType
  );

  const setActionConnect = () =>
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      actionPref: "CONNECT",
    }));

  const toast = useToast();

  const commonAnalyticsEventParams = useMemo(
    () => ({
      wallet_name: persistantData.connectorPref || "",
      wallet_address: `address:${account || "N/A"}`,
      token_balance: +providerData.walletData.tokenBalance || 0,
      liquidtoken_balance: +providerData.walletData.xTokenBalance || 0,
      Blockchain: isPolygonChainActive ? "Polygon Matic" : "Polygon ETH",
    }),
    [persistantData, account, providerData, isPolygonChainActive]
  );

  const track = useCallback(
    (
      eventName: EventNameString | string,
      eventParams: EventParams = {},
      options?: AnalyticsCallOptions
    ) => {
      trackEvent(
        eventName,
        { ...commonAnalyticsEventParams, ...eventParams },
        options
      );
    },
    [commonAnalyticsEventParams]
  );

  const openWalletModal = useCallback(
    (loc: string) => {
      track(OPEN_WALLET_MODAL, { wallet_cta: loc });
      setProviderData((prev) => ({ ...prev, modalType: "CONNECT_WALLET" }));
    },
    [track]
  );

  const openAccountModal = useCallback(() => {
    track(OPEN_ACCOUNT_MODAL);
    setProviderData((prev) => ({ ...prev, modalType: "ACCOUNT" }));
  }, [track]);

  const openAuditModal = useCallback(() => {
    track(OPEN_AUDIT_MODAL);
    setProviderData((prev) => ({ ...prev, modalType: "AUDIT" }));
  }, [track]);

  const closeModal = useCallback(() => {
    track(CLOSE_WALLET_MODAL, { type: providerData.modalType });
    setProviderData((prev) => ({ ...prev, modalType: "" }));
  }, [providerData.modalType, track]);

  const resetLatestTransactionInfo = () => {
    setProviderData((prev) => ({ ...prev, txnLoader: false }));
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      promptError: "",
      latestTxnHash: "",
    }));
  };

  const clearWalletData = () => {
    setProviderData((prev) => ({ ...prev, walletData: {} as WalletData }));
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      txnBatch: {},
      latestTxnHash: "",
      promptError: "",
    }));
  };

  const resetTxnInfoForNewTxn = () => {
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      promptError: "",
      latestTxnHash: "",
    }));
    setProviderData((prev) => ({ ...prev, txnLoader: true }));
  };

  const { isLedgerApp, isCheckingLedger } = useLedgerApp(config);

  const eagerTried = useEagerConnect(
    config,
    persistantData.actionPref,
    persistantData.connectorPref,
    selectedChain,
    (pref: string) =>
      setPersistantData((prev: PersistantDataType) => ({
        ...prev,
        connectorPref: pref,
      })),
    isLedgerApp,
    isCheckingLedger,
    track
  );

  const unsettledTransactions = _keys(persistantData.txnBatch).filter(
    (txn) => persistantData.txnBatch[txn].status === TXN_STATUSES.PENDING
  );

  const areTherePendingTxns = unsettledTransactions.length > 0;

  useEffect(() => {
    if (active && library && account && !_isEmpty(providerData.appData)) {
      (async function fetchData() {
        if (_isEmpty(providerData.walletData)) {
          setProviderData((prev) => ({ ...prev, balLoading: true }));
        }
        const signer = library.getSigner();

        try {
          const data = await getWalletData(
            config,
            signer,
            providerData.appData.currentEpoch || 0
          );

          setProviderData((prev) => ({
            ...prev,
            walletData: data,
            balLoading: false,
          }));
        } catch (error) {
          sentryErrorLog(error);
        }
      })();
    } else if (
      !_isEmpty(providerData.appData) &&
      _isEmpty(providerData.walletData) &&
      (!providerData.modalType || providerData.modalType === "AUDIT")
    ) {
      setProviderData((prev) => ({ ...prev, balLoading: false }));
    }
  }, [
    active,
    library,
    account,
    chainId,
    providerData.appData,
    selectedChain,
    persistantData.txnBatch,
  ]);

  useEffect(() => {
    (async function fetchData() {
      setProviderData((prev) => ({ ...prev, appLoading: true }));

      try {
        const data = await getAppData(config);
        setProviderData((prev) => ({
          ...prev,
          appData: data,
          appLoading: false,
        }));
      } catch (error) {
        sentryErrorLog(error);
      }
    })();
  }, [selectedChain]);

  useEffect(() => {
    if (!active && !_isEmpty(providerData.walletData)) {
      setProviderData((prev) => ({ ...prev, walletData: {} as WalletData }));
      setPersistantData((prev: PersistantDataType) => ({
        ...prev,
        txnBatch: {},
      }));
    }
  }, [active, providerData.walletData]);

  useEffect(() => {
    if (eagerTried && !active) {
      setPersistantData((prev: PersistantDataType) => ({
        ...prev,
        promptError: "",
        latestTxnHash: "",
      }));
    }
  }, [active, eagerTried]);

  useEffect(() => {
    if (active) {
      if (+selectedChain !== chainId) {
        switchtoValidNetwork(selectedChain);
      }
    }
  }, [active, selectedChain, chainId]);

  useEffect(() => {
    if (active && account && !providerData.balLoading) {
      Userpilot.track(ACTIVATE_SUCCESS, {
        ...commonAnalyticsEventParams,
      });
      track(ACTIVATE_SUCCESS);
    }
  }, [active, account, providerData.balLoading]);

  useEffect(() => {
    if (active && account && !providerData.balLoading) {
      if ((window as any).userpilotSettings.userid !== account) {
        Userpilot.identify(account, {
          ...commonAnalyticsEventParams,
        });
      }
    } else if (!account && !providerData.appLoading) {
      Userpilot.anonymous();
    }
  }, [
    active,
    account,
    providerData.balLoading,
    providerData.appLoading,
    commonAnalyticsEventParams,
  ]);

  useInterval(async () => {
    if (!isIdle) {
      (async function fetchData() {
        const data = await getAppData(config);

        setProviderData((prev) => ({ ...prev, appData: data }));
      })();
    }
  }, REFRESH_INTERVAL);

  useInterval(async () => {
    if (areTherePendingTxns) {
      (async function fetchConfirmation() {
        const confirmations = await Promise.all(
          unsettledTransactions.map((txn: string) =>
            getTransactionConfirmation(isPolygonChainActive, txn)
          )
        );

        confirmations.forEach((confirmation) => {
          if (confirmation) {
            const { type, hash, amount } =
              persistantData.txnBatch[confirmation.transactionHash];
            if (confirmation.blockNumber && confirmation.status) {
              track(TRANSACTION_SUCCESS, {
                amount,
                hash: `txn:${hash}`,
                txnType: type,
              });
              if (type === TXN_TYPES.STAKE) {
                Userpilot.track(STAKE_SUCCESS, {
                  amount,
                  hash: `txn:${hash}`,
                  ...commonAnalyticsEventParams,
                });
                track(PURCHASE_EVENT, {
                  value: +amount,
                  currency: "USD",
                });
              }
              updateTransactionDetailsWithResults(hash, TXN_STATUSES.SUCCESS);
              if (!providerData.txnLoader) {
                toast({
                  description: `${type} Transaction Successful`,
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: true,
                });
              }
            } else {
              track(TRANSACTION_FAILED, {
                amount,
                hash: `txn:${hash}`,
                txnType: type,
              });
              updateTransactionDetailsWithResults(hash, TXN_STATUSES.ERROR);
              if (!providerData.txnLoader) {
                toast({
                  description: `${type} Transaction failed`,
                  status: "error",
                  duration: 5000,
                  position: "top",
                  isClosable: true,
                });
              }
            }
          }
        });
      })();
    }
  }, REFRESH_INTERVAL_TXN);

  useEffect(() => {
    if (
      providerData.activatingConnector &&
      providerData.activatingConnector === connector
    ) {
      setProviderData((prev) => ({ ...prev, activatingConnector: undefined }));
    }
  }, [providerData.activatingConnector, connector]);

  useInactiveListener(
    account,
    library?.provider,
    persistantData.connectorPref,
    persistantData.actionPref === "CONNECT" &&
      (!eagerTried ||
        !!providerData.activatingConnector ||
        INJECTED_WALLETS.includes(persistantData.connectorPref)),
    config,
    setSelectedChain,
    clearWalletData
  );

  const updateTransactionDetails = (
    hash: string,
    type: string,
    amount: string | null,
    token: string
  ) => {
    const newTxn = {
      hash,
      status: TXN_STATUSES.PENDING,
      time: new Date(),
      amount,
      token,
      type,
    };
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      txnBatch: { ...prev.txnBatch, [hash]: newTxn },
      latestTxnHash: hash,
    }));
  };

  const updateTransactionDetailsWithResults = (
    hash: string,
    status: string
  ) => {
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      txnBatch: {
        ...prev.txnBatch,
        [hash]: { ...prev.txnBatch[hash], status },
      },
    }));
  };

  const switchtoValidNetwork = (chain: string) => {
    track(HANDLE_SWITCH_NETWORK, { chain });
    switchToMainnet(
      persistantData.connectorPref,
      library?.provider,
      chain,
      () => {
        if (connector) {
          activate(connector);
        }
      }
    );
  };

  const showInvalidNetworkToast = () => {
    toast({
      duration: 10000,
      position: "top",
      containerStyle: {
        width: "700px",
        maxWidth: "100%",
      },
      render: ({ onClose }) => (
        <Flex
          color="white"
          p={3}
          bg="#D9534F"
          borderRadius="0.5rem"
          alignItems="center"
          gap="1rem"
        >
          <Flex
            flexGrow={1}
            gap="1rem"
            alignItems="center"
            justifyContent="center"
          >
            <Text as="span">You are not connected to selected Network</Text>
            <Button
              bg="white"
              color="black"
              size="sm"
              _hover={{
                bg: "brand.primary",
                color: "white",
              }}
              onClick={() => {
                switchtoValidNetwork(selectedChain);
                onClose();
              }}
            >
              Switch Network
            </Button>
          </Flex>
          <CloseButton onClick={onClose} />
        </Flex>
      ),
    });
  };

  const isWalletandAppChainValid = active
    ? +selectedChain !== chainId
      ? false
      : true
    : false;

  const handleApprove = async () => {
    if (library) {
      try {
        track(HANDLE_APPROVE);

        resetTxnInfoForNewTxn();

        if (isWalletandAppChainValid) {
          let txn;

          if (isPolygonChainActive) {
            const currentMaticXAllowance = BigNumber.from(
              providerData.walletData.maticXAllowance || "0"
            );
            const isIncrement = currentMaticXAllowance.gt(0);

            txn = await addApprovalMaticX(
              config,
              library.getSigner(),
              isIncrement
                ? BigNumber.from(APPROVAL_AMOUNT)
                    .sub(currentMaticXAllowance)
                    .toString()
                : APPROVAL_AMOUNT,
              isIncrement
            );
          } else {
            const currentMaticAllowance = BigNumber.from(
              providerData.walletData.maxAllowance || "0"
            );
            const isIncrement = currentMaticAllowance.gt(0);

            txn = await addApproval(
              config,
              library.getSigner(),
              isIncrement
                ? BigNumber.from(APPROVAL_AMOUNT)
                    .sub(currentMaticAllowance)
                    .toString()
                : APPROVAL_AMOUNT,
              isIncrement
            );
          }

          updateTransactionDetails(
            txn.hash,
            TXN_TYPES.APPROVE,
            null,
            config.nativeCurrency.symbol
          );
        } else {
          showInvalidNetworkToast();
        }
      } catch (error: any) {
        sentryErrorLog(error);
        setPersistantData((prev: PersistantDataType) => ({
          ...prev,
          promptError: getErrorMessage(error),
        }));
      }
    }
  };

  const handleStake = async (amount: string) => {
    if (library) {
      try {
        Userpilot.track(SUBMIT_STAKE, {
          amount,
          ...commonAnalyticsEventParams,
        });
        track(SUBMIT_STAKE, {
          amount,
        });
        resetTxnInfoForNewTxn();

        if (isWalletandAppChainValid) {
          const useLowGas = +amount < providerData.appData.instantPool;

          const txn = await submitTransaction(
            config,
            library.getSigner(),
            amount,
            useLowGas,
            isPolygonChainActive
          );

          updateTransactionDetails(
            txn.hash,
            TXN_TYPES.STAKE,
            amount,
            config.nativeCurrency.symbol
          );
        } else {
          showInvalidNetworkToast();
        }
      } catch (error: any) {
        sentryErrorLog(error);
        setPersistantData((prev: PersistantDataType) => ({
          ...prev,
          promptError: getErrorMessage(error),
        }));
      }
    }
  };

  const handleUnstake = async (amount: string) => {
    if (library) {
      try {
        resetTxnInfoForNewTxn();

        if (isWalletandAppChainValid) {
          const txn = await requestWithdraw(
            config,
            library.getSigner(),
            amount,
            isPolygonChainActive
          );

          updateTransactionDetails(
            txn.hash,
            TXN_TYPES.UNSTAKE,
            amount,
            config.nativeCurrency.xsymbol
          );
        } else {
          showInvalidNetworkToast();
        }
      } catch (error: any) {
        sentryErrorLog(error);
        setPersistantData((prev: PersistantDataType) => ({
          ...prev,
          promptError: getErrorMessage(error),
        }));
      }
    }
  };

  const handleClaim = async (claimId: number, amount: number) => {
    if (library) {
      try {
        track(CLICK_CLAIM_BTN, {
          withdraw_amount: amount,
        });
        resetTxnInfoForNewTxn();

        if (isWalletandAppChainValid) {
          const txn = await claimWithdrawal(
            config,
            library.getSigner(),
            claimId,
            isPolygonChainActive
          );

          updateTransactionDetails(
            txn.hash,
            TXN_TYPES.CLAIM,
            amount.toString(),
            config.nativeCurrency.symbol
          );
        } else {
          showInvalidNetworkToast();
        }
      } catch (error: any) {
        sentryErrorLog(error);
        setPersistantData((prev: PersistantDataType) => ({
          ...prev,
          promptError: getErrorMessage(error),
        }));
      }
    }
  };

  const handleDisconnect = useCallback(() => {
    try {
      track(HANDLE_DISCONNECT);
      deactivate();
      setProviderData((prev) => ({ ...prev, walletData: {} as WalletData }));
      setPersistantData((prev: PersistantDataType) => ({
        ...prev,
        txnBatch: {},
        connectorPref: "",
        actionPref: "DISCONNECT",
      }));
      closeModal();
    } catch (error: any) {
      toast({
        description: error,
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    }
  }, [deactivate, closeModal]);

  const handleActivate = useCallback(
    async (key: string, cb: any) => {
      const connectorKey = isZerion(key) ? "zerion" : key;

      track(HANDLE_ACTIVATE, { wallet_name: connectorKey });
      setProviderData((prev) => ({ ...prev, balLoading: true }));

      const connector = getConnector(key, config);

      try {
        await activate(connector, (error) => {
          track(ACTIVATE_FAILED, { wallet_name: connectorKey });
          throw error;
        });

        setActionConnect();
        setProviderData((prev) => ({
          ...prev,
          activatingConnector: connector,
        }));
        setPersistantData((prev: PersistantDataType) => ({
          ...prev,
          connectorPref: connectorKey,
        }));
        cb();
      } catch (error: any) {
        const errorMsg = getErrorMessage(error);

        deactivate();
        setProviderData((prev) => ({ ...prev, balLoading: false }));

        const isUnsupportedChain = error instanceof UnsupportedChainIdError;

        toast({
          duration: 10000,
          position: "top",
          containerStyle: {
            width: "700px",
            maxWidth: "100%",
          },
          render: ({ onClose }) => (
            <Flex
              color="white"
              p={3}
              bg="#D9534F"
              borderRadius="0.5rem"
              alignItems="center"
              gap="1rem"
            >
              <Flex
                flexGrow={1}
                gap="1rem"
                alignItems="center"
                justifyContent="center"
              >
                <Text as="span">{errorMsg}</Text>
                {isUnsupportedChain && (
                  <Button
                    bg="white"
                    color="black"
                    size="sm"
                    _hover={{
                      bg: "brand.primary",
                      color: "white",
                    }}
                    onClick={() => {
                      switchToMainnet(
                        key,
                        library?.provider,
                        config.chainId,
                        () => handleActivate(key, cb)
                      );
                      onClose();
                    }}
                  >
                    Switch Network
                  </Button>
                )}
              </Flex>
              <CloseButton onClick={onClose} />
            </Flex>
          ),
        });
      }
    },
    [activate, deactivate]
  );

  const handleAddressCopy = useCallback(() => {
    track(COPY_ACCOUNT_ADDRESS);
    copyToClipboard(account);

    toast({
      description: "Address Copied",
      status: "success",
      duration: 2000,
      position: "top",
      isClosable: true,
    });
  }, [account]);

  const handleViewOnEtherScan = useCallback(() => {
    track(VIEW_ADDRESS_ON_SCANNER);
    const url = `${config.blockExplorerUrls}/address/${account}`;

    window.open(url, "_blank");
  }, [account]);

  const handleTxViewOnEtherScan = (hash = persistantData.latestTxnHash) => {
    track(VIEW_TXN_ON_SCANNER);
    const url = `${config.blockExplorerUrls}/tx/${hash}`;

    window.open(url, "_blank");
  };

  const addTokenToUserWallet = async () => {
    if (library) {
      track(CLICK_STAKE_ADD_TOKEN);
      const provider = library.provider;

      addXTokenToWallet(config, provider);
    }
  };

  const switchToPolygon = () => {
    if (connector) {
      switchToMainnet(
        persistantData.connectorPref,
        library?.provider,
        selectedChain,
        () => activate(connector)
      );
    }
  };

  const walletInfo = {
    tokenBalance: +providerData.walletData.tokenBalance || 0,
    xTokenBalance: +providerData.walletData.xTokenBalance || 0,
    tokenSymbol: config.nativeCurrency.symbol,
    xTokenSymbol: config.nativeCurrency.xsymbol,
    blockchain: config.nativeCurrency.name,
    icon: currentNetworkInfo.icon[colorMode],
    xtokenIcon: currentNetworkInfo.tokenXIcon[colorMode],
    address: account || "",
  };

  const providerInfo = {
    name: persistantData.connectorPref
      ? walletLabelMapping[persistantData.connectorPref]
      : "",
    icon: persistantData.connectorPref
      ? walletIconMapping[persistantData.connectorPref][colorMode]
      : null,
  };

  const contextData = {
    selectedChain,
    toggleChain,
    config,
    isPolygonChainActive,
    walletInfo,
    providerInfo,
    handleActivate,
    handleDisconnect,
    handleAddressCopy,
    handleViewOnEtherScan,
    handleApprove,
    handleStake,
    handleUnstake,
    handleClaim,
    addTokenToUserWallet,
    openWalletModal,
    openAccountModal,
    openAuditModal,
    closeModal,
    loading: providerData.appLoading,
    balLoading: providerData.balLoading,
    tvl: providerData.appData.tvl,
    conversionRate: providerData.appData.conversionRate,
    conversionFromRate: providerData.appData.conversionFromRate,
    maxAllowance: providerData.walletData.maxAllowance || "0",
    maticXAllowance: providerData.walletData.maticXAllowance || "0",
    withdrawals: providerData.walletData.withdrawals || [],
    crossChainTVL: providerData.appData.crossChainTVL,
    instantPool: providerData.appData.instantPool,
    instantPoolMatic: providerData.appData.instantPoolMatic,
    isTxnProcessing: areTherePendingTxns,
    isTxnModalOpen: providerData.txnLoader,
    openTxnHistoryModal: () =>
      setProviderData((prev) => ({ ...prev, isTxnBatchOpen: true })),
    stakingLimitDetails: {
      totalLimit: providerData.appData?.stakingLimitDetails?.totalLimit || 0,
      exhaustedLimit:
        providerData.appData?.stakingLimitDetails?.exhaustedLimit || 0,
    },
    isValidChainSelected: chainId === +selectedChain,
    switchToPolygon,
    connectorName: persistantData.connectorPref,
    isLedgerApp,
    track,
  };

  if (!providerData.appData.tvl || isCheckingLedger) {
    return <Loader />;
  }

  return (
    <StakeContext.Provider value={contextData}>
      {children}
      <WalletConnectModal
        isOpen={providerData.modalType === "CONNECT_WALLET" && !active}
        walletMenuOptions={getWalletOptions(isPolygonChainActive)}
      />
      <AccountModal isOpen={providerData.modalType === "ACCOUNT" && active} />
      <TransactionModal
        isOpen={providerData.txnLoader}
        hash={persistantData.latestTxnHash}
        error={
          persistantData.promptError ||
          (persistantData.txnBatch[persistantData.latestTxnHash] &&
            persistantData.txnBatch[persistantData.latestTxnHash].status ===
              TXN_STATUSES.ERROR)
        }
        isTxnProcessing={
          persistantData.txnBatch[persistantData.latestTxnHash]
            ? persistantData.txnBatch[persistantData.latestTxnHash].status ===
              TXN_STATUSES.PENDING
            : true
        }
        closeAlert={resetLatestTransactionInfo}
        handleTxView={handleTxViewOnEtherScan}
      />
      <AllTransaction
        isOpen={providerData.isTxnBatchOpen}
        onClose={() =>
          setProviderData((prev) => ({ ...prev, isTxnBatchOpen: false }))
        }
        handleTxView={handleTxViewOnEtherScan}
        txnBatchData={_values(persistantData.txnBatch).sort(
          (txn1, txn2) =>
            new Date(txn2.time).getTime() - new Date(txn1.time).getTime()
        )}
        onClear={() => {
          track(CLEAR_PAST_TRANSACTIONS);
          setPersistantData((prev: PersistantDataType) => ({
            ...prev,
            txnBatch: {},
          }));
        }}
      />
      <AuditInfoModal isOpen={providerData.modalType === "AUDIT"} />
    </StakeContext.Provider>
  );
};
